import { applyMiddleware, compose, createStore } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { history } from 'store/history'
import createReducerManager from 'store/reducerManager'
import { getStaticReducers } from 'store/staticReducers'
import { rootStaticSaga } from 'store/staticSagas'

const composeEnhancers =
  (process.env.NODE_ENV !== 'production' &&
    global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
    global.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      trace: true,
      traceLimit: 25,
    })) ||
  compose

const sagaMiddleware = createSagaMiddleware()
const middlewares = [sagaMiddleware]
const reducerManager = createReducerManager(getStaticReducers())
const injectedSagas = new Map()

export { history }

export const INIT_INJECTED_REDUCERS = 'STORE/INIT_INJECTED_REDUCERS'

const rootReducer = (state, action) => {
  if (action.type === INIT_INJECTED_REDUCERS) {
    const reducerMap = reducerManager.getReducerMap()
    let nextState = state

    Object.keys(reducerMap).forEach((key) => {
      if (nextState?.[key] !== undefined) {
        return
      }

      nextState = {
        ...nextState,
        [key]: reducerMap[key](undefined, action),
      }
    })

    return nextState
  }

  return reducerManager.reduce(state, action)
}

export const store = createStore(rootReducer, composeEnhancers(applyMiddleware(...middlewares)))

store.reducerManager = reducerManager

export const injectReducers = (reducers = {}) => {
  const reducerMap = reducerManager.getReducerMap()
  let hasNewReducer = false

  Object.entries(reducers).forEach(([key, reducer]) => {
    if (!key || reducerMap[key]) {
      return
    }

    reducerManager.add(key, reducer)
    hasNewReducer = true
  })

  // combineReducers only materializes slice state on dispatch. Without this, the first
  // render after lazy inject sees undefined for any dynamically added key (all domains).
  const state = store.getState()
  const hasUninitializedSlice = Object.keys(reducers).some(
    (key) => key && reducerManager.getReducerMap()[key] && state[key] === undefined,
  )

  if (hasNewReducer || hasUninitializedSlice) {
    store.dispatch({ type: INIT_INJECTED_REDUCERS })
  }
}

/**
 * @deprecated Use createDomainSelectors instead for type-safe, per-domain selectors.
 */
export const selectSlice = (state, key, fallback = null) => state[key] ?? fallback

export const injectSagas = (sagas = {}) => {
  Object.entries(sagas).forEach(([key, saga]) => {
    if (!saga || injectedSagas.has(key)) {
      return
    }

    injectedSagas.set(key, sagaMiddleware.run(saga))
  })
}

sagaMiddleware.run(rootStaticSaga)
